import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * Consistent page header used on all non-home pages.
 *
 * Usage:
 *   <PageHero
 *     eyebrow="Shop"
 *     title={<>Everything <span className="text-gold">gaming</span></>}
 *     description="Optional subline"
 *     breadcrumbs={[{ to: "/", label: "Home" }, { label: "Shop" }]}
 *   />
 */
export default function PageHero({
  eyebrow,
  title,
  description,
  breadcrumbs,
  align = "left",
  size = "default", // "default" | "compact"
  children,
}) {
  const isCompact = size === "compact";
  return (
    <section className="relative overflow-hidden border-b border-gold/10 bg-navy-900">
      {/* Decorative background */}
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      <div
        className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(245,205,48,0.12), transparent 70%)" }}
      />
      <div
        className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(30,40,93,0.7), transparent 70%)" }}
      />

      <div
        className={`relative site-container ${isCompact ? "py-10" : "py-16 sm:py-20"} ${
          align === "center" ? "text-center" : ""
        }`}
      >
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-4">
            {breadcrumbs.map((b, i) => (
              <React.Fragment key={i}>
                {i > 0 && <ChevronRight className="w-3 h-3 text-slate-600" />}
                {b.to ? (
                  <Link to={b.to} className="hover:text-gold transition">
                    {b.label}
                  </Link>
                ) : (
                  <span className="text-gold">{b.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        )}

        {eyebrow && (
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-3">{eyebrow}</div>
        )}

        <h1
          className={`font-black tracking-tighter uppercase leading-[0.95] ${
            isCompact
              ? "text-3xl sm:text-4xl lg:text-5xl"
              : "text-4xl sm:text-5xl lg:text-6xl"
          }`}
        >
          {title}
        </h1>

        {description && (
          <p className={`mt-4 text-sm sm:text-base text-slate-300 leading-relaxed max-w-2xl ${align === "center" ? "mx-auto" : ""}`}>
            {description}
          </p>
        )}

        {children && <div className="mt-6">{children}</div>}
      </div>
    </section>
  );
}
